import * as React from 'react';
import { useState, useEffect, useMemo, useRef } from 'react';
import { TextField, CircularProgress, InputAdornment } from '@material-ui/core';
import Info from '@material-ui/icons/Info';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { FieldValidation } from '@rjsf/utils';
import { ApiHolder } from '@backstage/core-plugin-api';
import { belongsToParent } from '../../utils/catalogEntityHelper';
// import { mlflowApiRef } from '@internal/backstage-plugin-mlflow'; // Commented for future use

// Validation function for form-level validation (blocks navigation in scaffolder forms)
export const validateModelName = async (
  value: string,
  validation: FieldValidation,
  context: { apiHolder: ApiHolder },
) => {
  const currentValue = value || '';
  
  if (!currentValue) return; // Let required validation handle empty values

  // Default schema values (can be overridden by schema in form)
  const minLength = 3;
  const maxLength = 63;
  const pattern = '^[A-Za-z0-9-]+$';

  // Basic validations - return early after first error
  if (currentValue.length < minLength) {
    validation.addError(`Model name must be at least ${minLength} characters long`);
    return;
  }

  if (currentValue.length > maxLength) {
    validation.addError(`Model name must be less than ${maxLength} characters`);
    return;
  }

  if (!new RegExp(pattern).test(currentValue)) {
    validation.addError('Model name must be 3-63 characters long and contain only letters, numbers, and hyphens');
    return;
  }

  // Duplicate check - only run if basic validations pass
  try {
    const catalogApi = context.apiHolder.get(catalogApiRef);
    if (!catalogApi) {
      validation.addError('Unable to validate model name. Please try again.');
      return;
    }
    
    const entities = await catalogApi.getEntities({
      filter: {
        kind: 'Component',
        'metadata.name': currentValue.toLowerCase(),
      },
    });

    if (entities.items.length > 0) {
      const existingEntity = entities.items[0];
      const entityType = existingEntity.spec?.type;

      // If type is NOT "model", block duplicate like CustomAppNameField
      if (entityType !== 'model') {
        validation.addError(`A component with name "${currentValue}" already exists in the catalog`);
        return;
      }

      // For models, we allow duplicates (version bumping)
      // In scaffolder forms, we can't easily check parent project context,
      // so we allow model duplicates and let the component handle the parent check
      // when used in custom forms like MLModelTrain
      // This is informational, not an error - allow to proceed
    }
  } catch (error) {
    validation.addError('Unable to validate model name. Please try again.');
  }
};

export interface CustomModelNameFieldProps extends FieldExtensionComponentProps<string> {
  onValidationChange?: (isValid: boolean, errorMessage?: string) => void;
}

export const CustomModelNameField = ({
  onChange,
  required,
  formData,
  schema,
  rawErrors,
  onValidationChange,
}: CustomModelNameFieldProps) => {
  const [validationState, setValidationState] = useState<'idle' | 'validating' | 'valid' | 'invalid' | 'info'>('idle');
  const [validationMessage, setValidationMessage] = useState<string>('');
  const [debouncedValue, setDebouncedValue] = useState(formData || '');
  const lastValueRef = useRef(formData || '');
  const catalogApi = useApi(catalogApiRef);
  // const mlflowApi = useApi(mlflowApiRef); // Commented for future use

  // Extract schema properties
  const {
    title,
    description,
    pattern,
    minLength,
    maxLength,
    placeholder,
    helperText,
    errorMessage,
    currentProjectName,
  } = schema;

  // Debounce effect to prevent duplicate validation requests
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(formData || '');
    }, 500);

    return () => clearTimeout(timer);
  }, [formData]);

  // Clear validation state when input changes
  useEffect(() => {
    if (formData !== debouncedValue) {
      setValidationState('idle');
      setValidationMessage('');
    }
  }, [formData, debouncedValue]);

  // Check for form validation errors (these block navigation)
  const hasFormValidationErrors = rawErrors && rawErrors.length > 0;

  // Handle input change and try to clear form errors
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    
    // If the value has changed and there were form validation errors, try to trigger re-validation
    if (newValue !== lastValueRef.current && hasFormValidationErrors) {
      // Force a re-validation by calling onChange with a slight delay
      setTimeout(() => {
        onChange(newValue);
      }, 10);
    }
    
    lastValueRef.current = newValue;
    onChange(newValue);
  };

  // Real-time validation for UI feedback (visual only) - only run when no form errors
  const validateName = useMemo(() => async (name: string) => {
    // Don't run field validation if form validation errors are present
    if (hasFormValidationErrors) {
      return;
    }

    if (!name || name.length < 2) {
      setValidationState('idle');
      setValidationMessage('');
      return;
    }

    // Basic validation first
    if (minLength && name.length < minLength) {
      setValidationState('invalid');
      setValidationMessage(`Name must be at least ${minLength} characters long`);
      return;
    }

    if (maxLength && name.length > maxLength) {
      setValidationState('invalid');
      setValidationMessage(`Name must be less than ${maxLength} characters`);
      return;
    }

    if (pattern && !new RegExp(pattern).test(name)) {
      setValidationState('invalid');
      setValidationMessage(errorMessage || 'Name contains invalid characters');
      return;
    }

    setValidationState('validating');
    try {
      // Check catalog for existing component with this name
      const entities = await catalogApi.getEntities({
        filter: {
          kind: 'Component',
          'metadata.name': name.toLowerCase(),
        },
      });

      if (entities.items.length > 0) {
        const existingEntity = entities.items[0];
        const entityType = existingEntity.spec?.type;

        // If type is NOT "model", block duplicate like CustomAppNameField
        if (entityType !== 'model') {
          setValidationState('invalid');
          setValidationMessage(`A component with name "${name}" already exists in the catalog`);
          return;
        }

        // For models, check parent project relationship
        if (currentProjectName) {
          const belongsToCurrentProject = await belongsToParent(
            existingEntity,
            currentProjectName,
            catalogApi,
            name.toLowerCase()
          );

          if (belongsToCurrentProject) {
            // Parent matches - allow version bump
            setValidationState('info');
            setValidationMessage('A model with this name already exists. A new version will be created.');
          } else {
            // Parent doesn't match - it's a different project's model, allow to proceed
            // Same model names can exist in different projects
            setValidationState('valid');
            setValidationMessage('');
          }
        } else {
          // No current project context - default to blocking duplicate
          setValidationState('invalid');
          setValidationMessage(`A component with name "${name}" already exists in the catalog`);
        }
      } else {
        setValidationState('valid');
        setValidationMessage('');
      }

      // MLFlow API check - commented for future use
      // try {
      //   // Try to get the model - if it exists, show info message
      //   await mlflowApi.getRegisteredModel(name);
      //   
      //   // Model exists - show info message (not an error)
      //   setValidationState('info');
      //   setValidationMessage('A model with this name already exists. A new version will be created.');
      // } catch (error) {
      //   // Check if it's a 404 (model doesn't exist) - that's valid
      //   if (error instanceof Error && (error.message.includes('404') || error.message.includes('not found'))) {
      //     setValidationState('valid');
      //     setValidationMessage('');
      //   } else {
      //     // Other errors - show as validation error
      //     setValidationState('invalid');
      //     setValidationMessage('Unable to validate model name');
      //   }
      // }
    } catch (error) {
      setValidationState('invalid');
      setValidationMessage('Unable to validate model name');
    }
  }, [catalogApi, minLength, maxLength, pattern, errorMessage, hasFormValidationErrors, currentProjectName]);

  useEffect(() => {
    validateName(debouncedValue || '');
  }, [debouncedValue, validateName]);

  // Notify parent component of validation state changes
  useEffect(() => {
    if (onValidationChange) {
      // Field is invalid if:
      // 1. Validation state is 'invalid'
      // 2. There are form validation errors
      // 3. Field is required and empty (but let required validation handle that)
      const isValid = validationState !== 'invalid' && !hasFormValidationErrors;
      const errorMsg = (validationState === 'invalid' || hasFormValidationErrors) 
        ? (validationMessage || (hasFormValidationErrors && rawErrors?.[0]) || '') 
        : undefined;
      onValidationChange(isValid, errorMsg);
    }
  }, [validationState, validationMessage, hasFormValidationErrors, rawErrors, onValidationChange]);

  const getEndAdornment = () => {
    // If form validation errors exist, don't show any field-level icons
    if (hasFormValidationErrors) {
      return <div />;
    }
    
    switch (validationState) {
      case 'validating':
        return <CircularProgress size={20} />;
      case 'valid':
        return <div />; // No icon for valid state
      case 'invalid':
        return <div />; // No icon for invalid state (error styling is enough)
      case 'info':
        return <Info style={{ color: '#ff9800' }} />; // Orange info icon
      default:
        return <div />;
    }
  };

  // Only show field-level errors when there are no form validation errors
  const hasError = hasFormValidationErrors || (!hasFormValidationErrors && validationState === 'invalid');
  
  // Determine helper text with appropriate styling
  let displayHelperText = '';
  if (hasFormValidationErrors) {
    displayHelperText = '';
  } else if (validationState === 'info') {
    displayHelperText = validationMessage;
  } else if (validationState === 'invalid') {
    displayHelperText = validationMessage;
  } else {
    displayHelperText = helperText || description || '';
  }

  return (
    <TextField
      fullWidth
      label={title}
      value={formData || ''}
      onChange={handleInputChange}
      error={hasError}
      helperText={
        <span style={{ color: validationState === 'info' ? '#ff9800' : undefined }}>
          {displayHelperText}
        </span>
      }
      placeholder={placeholder}
      required={required}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            {getEndAdornment()}
          </InputAdornment>
        ),
      }}
    />
  );
};

